// src/ManageStaff.js - VERSIONE A SCHEDE UNIFICATE

import React, { useState, useEffect } from 'react';
import { db, auth, functions } from './firebase'; 
import { httpsCallable } from "firebase/functions";
import { collection, getDocs, addDoc, updateDoc, doc, deleteDoc, setDoc } from 'firebase/firestore';
import { StaffModal } from './StaffModal';
import { toast } from 'react-toastify';
import { AssociateAccountModal } from './AssociateAccountModal';
import { useUser } from './UserContext';
import { logAction } from './loggingService';
import { getPermissionablePages } from './appConfig';
import { useNavigate } from 'react-router-dom';
import { ChoiceModal } from './Modals';
import { ConfirmationModal } from './Modals';
import moment from 'moment';           
import { v4 as uuidv4 } from 'uuid';

// 🆕 Import per i nuovi tab e icone
import { Users, Stethoscope, Briefcase } from 'lucide-react';
import ManageDoctors from './ManageDoctors';
import ManageContractProfiles from './ManageContractProfiles';

const didSlotChange = (oldSlot, newSlot) => {
    if (!oldSlot || !newSlot) return false; 
    return (
        oldSlot.name !== newSlot.name ||
        oldSlot.startTime !== newSlot.startTime ||
        oldSlot.endTime !== newSlot.endTime ||
        oldSlot.pauseMinutes !== newSlot.pauseMinutes ||
        oldSlot.isRecovery !== newSlot.isRecovery ||
        oldSlot.excludeFromAutoCalc !== newSlot.excludeFromAutoCalc
    );
};

const hasRelevantSlotChanged = (oldSlots, newSlots) => {
    if (!oldSlots || !newSlots) return true;
    if (oldSlots.length !== newSlots.length) return true;
    const oldMap = new Map(oldSlots.map(s => [s.id, s]));

    for (const newSlot of newSlots) {
        const oldSlot = oldMap.get(newSlot.id);
        if (!oldSlot) return true; 
        if (
            oldSlot.name !== newSlot.name ||
            oldSlot.startTime !== newSlot.startTime ||
            oldSlot.endTime !== newSlot.endTime ||
            oldSlot.pauseMinutes !== newSlot.pauseMinutes ||
            oldSlot.isRecovery !== newSlot.isRecovery ||
            oldSlot.excludeFromAutoCalc !== newSlot.excludeFromAutoCalc
        ) {
            return true;
        }
    }
    return false;
};

// 🆕 Helper per i bottoni delle Tab
const TabButton = ({ label, icon, isActive, onClick }) => (
    <button onClick={onClick} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '1rem', border: 'none', background: 'none', borderBottom: isActive ? '2px solid #2563eb' : '2px solid transparent', color: isActive ? '#2563eb' : '#6b7280', fontWeight: isActive ? 'bold' : '500', cursor: 'pointer', whiteSpace: 'nowrap', outline: 'none' }}>
        {icon} {label}
    </button>
);

function ManageStaff() {
    const { userProfile } = useUser();
    const navigate = useNavigate();
    
    // 🆕 Stato del Tab attivo
    const [activeTab, setActiveTab] = useState('staff');

    const [staffList, setStaffList] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingStaff, setEditingStaff] = useState(null);
    const [copiedSlots, setCopiedSlots] = useState(null);
    const [associationModal, setAssociationModal] = useState({ isOpen: false, staff: null });
    const [resetPwdModal, setResetPwdModal] = useState({ isOpen: false, staff: null });
    const staffPermission = userProfile?.permissions?.staff || 'none';

    const changeUserEmailFn = httpsCallable(functions, 'changeUserEmail');
    const resetUserPasswordFn = httpsCallable(functions, 'resetUserPassword');
    const createStaffAccountFn = httpsCallable(functions, 'createStaffAccount');
    const applyTimeSlotChangeToSchedule = httpsCallable(functions, 'applyTimeSlotChangeToSchedule');
    const [originalStaffData, setOriginalStaffData] = useState(null); 
    const [modal, setModal] = useState({ type: null, data: null }); 

    const fetchStaff = async () => {
        if (!userProfile || !userProfile.centerId) {
            setStaffList([]); 
            return;
        }
        const staffCollectionRef = collection(db, 'centers', userProfile.centerId, 'staff');
        const querySnapshot = await getDocs(staffCollectionRef);
        const staff = querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }))
        // 🆕 AGGIUNGI QUESTA RIGA PER ESCLUDERE I MEDICI:
        .filter(s => s.role !== 'medico' && s.role !== 'Medico') 
        .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)); 
        
        setStaffList(staff);
    };

    useEffect(() => {
        fetchStaff();
    }, [userProfile]);

    const handleSaveStaff = async (staffData) => {
        const dataToSave = {
            name: staffData.name,
            color: staffData.color,
            role: staffData.role,
            assignmentType: staffData.assignmentType || 'rotation', 
            timeSlots: staffData.timeSlots,
            displayOrder: staffData.displayOrder,
            permissions: staffData.permissions,
            includeInShifts: staffData.includeInShifts,
            personalData: staffData.personalData || {},
            contractData: staffData.contractData || {},
            bankData: staffData.bankData || {},
            leaveBalances: staffData.leaveBalances || {},
            ...(staffData.email && { email: staffData.email }),
            centerId: userProfile.centerId,
        };
        Object.keys(dataToSave).forEach(key => dataToSave[key] === undefined && delete dataToSave[key]);

        const isEditingExisting = staffList.some(s => s.id === staffData.id);
        const originalData = originalStaffData || {}; 
        const slotsChanged = isEditingExisting && hasRelevantSlotChanged(originalData.timeSlots, staffData.timeSlots);

        if (!isEditingExisting || !slotsChanged) {
             const staffDocRef = doc(db, 'centers', userProfile.centerId, 'staff', staffData.id);
             await updateDoc(staffDocRef, dataToSave);
             await logAction(userProfile, `ha modificato i dati di: ${staffData.name}`);
             setIsModalOpen(false);
             setOriginalStaffData(null);
             fetchStaff();
             return;
        }

        if (slotsChanged) {
            setModal({ 
                type: 'recalc-staff-slots', 
                data: {
                    userId: staffData.id,
                    newTimeSlots: staffData.timeSlots, 
                    dataToSave: dataToSave, 
                    userName: staffData.name
                } 
            });
            setIsModalOpen(false); 
            return;
        }
    };

    const handleRecalculateChoice = async (choice, data) => {
        const { userId, dataToSave, userName } = data;
        const today = moment().format('YYYY-MM-DD');
        const yesterday = moment().subtract(1, 'day').format('YYYY-MM-DD');
        const finalDataToSave = JSON.parse(JSON.stringify(dataToSave));
        const originalSlots = originalStaffData?.timeSlots || [];
        const newSlotsFromModal = finalDataToSave.timeSlots || [];
        const finalSlots = [];

        if (choice === 'retrospective') {
            newSlotsFromModal.forEach(newSlot => {
                const oldSlot = originalSlots.find(s => s.id === newSlot.id);
                if (oldSlot && didSlotChange(oldSlot, newSlot)) {
                    if (newSlot.pauseMinutes === 0 || newSlot.pauseMinutes === '0') newSlot.pauseStartTime = ''; 
                    finalSlots.push({ ...newSlot, validFrom: oldSlot.validFrom || "2020-01-01", validUntil: null });
                } 
                else if (oldSlot) { finalSlots.push(oldSlot); }
                else { finalSlots.push({ ...newSlot, validFrom: newSlot.validFrom || "2020-01-01", validUntil: newSlot.validUntil === undefined ? null : newSlot.validUntil }); }
            });

            originalSlots.forEach(oldSlot => {
                if (!newSlotsFromModal.some(s => s.id === oldSlot.id)) finalSlots.push(oldSlot); 
            });

            finalDataToSave.timeSlots = finalSlots;
            const staffDocRef = doc(db, 'centers', userProfile.centerId, 'staff', userId);
            await updateDoc(staffDocRef, finalDataToSave); 
            await logAction(userProfile, `ha modificato STORICAMENTE le fasce orarie di: ${userName}`);
            toast.success("Fasce orarie aggiornate retroattivamente!");

            toast.info("Avvio del ricalcolo degli orari pregressi in background...");
            try {
                applyTimeSlotChangeToSchedule({ userId, centerId: userProfile.centerId })
                    .then(res => { toast.success("Ricalcolo pregresso completato!"); })
                    .catch(error => { toast.error(`Errore durante il ricalcolo pregresso: ${error.message}`); });
            } catch (error) { toast.error("Impossibile avviare il ricalcolo."); }

        } else {
            newSlotsFromModal.forEach(newSlot => {
                const oldSlot = originalSlots.find(s => s.id === newSlot.id);
                if (!oldSlot) {
                    finalSlots.push({ ...newSlot, validFrom: newSlot.validFrom ? newSlot.validFrom : today, validUntil: null });
                } else if (didSlotChange(oldSlot, newSlot)) {
                    finalSlots.push({ ...oldSlot, validUntil: yesterday });
                    if (newSlot.pauseMinutes === 0 || newSlot.pauseMinutes === '0') newSlot.pauseStartTime = ''; 
                    finalSlots.push({ ...newSlot, id: uuidv4(), validFrom: today, validUntil: null });
                } else {
                    finalSlots.push(oldSlot);
                }
            });

            originalSlots.forEach(oldSlot => {
                const wasDeleted = !newSlotsFromModal.some(s => s.id === oldSlot.id);
                if (wasDeleted) finalSlots.push({ ...oldSlot, validUntil: yesterday });
            });

            finalDataToSave.timeSlots = finalSlots;
            const staffDocRef = doc(db, 'centers', userProfile.centerId, 'staff', userId);
            await updateDoc(staffDocRef, finalDataToSave); 
            await logAction(userProfile, `ha modificato (solo futuro) le fasce orarie di: ${userName}`);
            toast.success("Fasce orarie aggiornate solo per il futuro!");
        }

        setOriginalStaffData(null); 
        setModal({ type: null, data: null });
        fetchStaff();
    };

    const handleDeleteStaff = async (staffId) => {
        const staffToDelete = staffList.find(s => s.id === staffId); 
        if (window.confirm("Sei sicuro di voler eliminare questa persona e il suo account di accesso? L'azione è irreversibile.")) {
            const staffDocRef = doc(db, 'centers', userProfile.centerId, 'staff', staffId); 
            await deleteDoc(staffDocRef);
            if (staffToDelete) {
               await logAction(userProfile, `ha eliminato il membro dello staff: ${staffToDelete.name}`);
            }
            fetchStaff();
        }
    };
    
    const handleCopySlots = (slots) => {
        setCopiedSlots(slots);
        toast.success("Orari copiati!", { position: "bottom-center", autoClose: 2000, hideProgressBar: true });
    };

    const handlePasteSlots = async (staffId) => {
        if (!copiedSlots) { alert("Nessun orario è stato copiato. Prima usa il pulsante 'Copia Orari'."); return; }
        if (window.confirm("Sei sicuro di voler sovrascrivere gli orari di questa persona?")) {
            const staffDocRef = doc(db, 'centers', userProfile.centerId, 'staff', staffId); 
            await updateDoc(staffDocRef, { timeSlots: copiedSlots });
            fetchStaff();
        }
    };

        // 🐛 FIX: blocca i click ripetuti mentre una creazione account è già in
    // corso — senza questa guardia, un doppio click (o un tocco impaziente su
    // schermi lenti) può far partire due chiamate quasi simultanee a
    // createStaffAccount con la stessa email, con risultati imprevedibili
    // (nel peggiore dei casi, un utente Auth "orfano" senza staff/userLookup
    // corrispondenti, o due account distinti se la corsa capita nel momento
    // sbagliato lato server).
    const [isCreatingAccount, setIsCreatingAccount] = useState(false);

    const handleAssociateAccount = async (email, password) => {
        if (isCreatingAccount) return; // Ignora click ripetuti mentre è già in corso
        const oldStaffData = associationModal.staff;
        if (!oldStaffData) return;

        setIsCreatingAccount(true);
        const promise = createStaffAccountFn({ 
            email: email, password: password, oldDocId: oldStaffData.id, centerId: userProfile.centerId 
        });

        toast.promise(promise, {
            pending: 'Creazione account in corso...',
            success: `Account per ${oldStaffData.name} creato con successo!`,
            error: 'Errore durante la creazione dell\'account.'
        });

        try {
            await promise;
            fetchStaff();
        } catch (error) {
            console.error("Errore frontend durante la creazione utente:", error);
        } finally {
            setIsCreatingAccount(false);
            setAssociationModal({ isOpen: false, staff: null });
        }
    };

    const handleEditClick = (staffMember) => { 
        setEditingStaff(staffMember);
        setOriginalStaffData(staffMember); 
        setIsModalOpen(true);
    };

    const handleNewClick = async () => {
        const tempDoc = await addDoc(collection(db, 'centers', userProfile.centerId, 'staff'), { 
             name: "Nuova Persona", color: "#cccccc", role: "Segretaria", displayOrder: 99, timeSlots: [],
             permissions: { level: 'viewer', pages: { shifts: true } }, includeInShifts: true, centerId: userProfile.centerId, 
        });
        await fetchStaff();
        setEditingStaff({ id: tempDoc.id, name: "Nuova Persona" });
        setIsModalOpen(true);
    };

    const handleChangeEmail = async (staff) => {
        const newEmail = prompt(`Inserisci la nuova email per ${staff.name}:`, staff.email || "");
        if (newEmail && newEmail.trim() !== "") {
            const promise = changeUserEmailFn({ uid: staff.id, newEmail: newEmail.trim(), centerId: userProfile.centerId }); 
            toast.promise(promise, {
                pending: 'Aggiornamento email in corso...', success: 'Email aggiornata con successo!', error: 'Errore.'
            });
            try { await promise; fetchStaff(); } catch (error) {}
        }
    };

    const handleResetPassword = async (staff) => {
        try {
            await toast.promise(
                resetUserPasswordFn({ uid: staff.id, centerId: userProfile.centerId }), 
                { pending: 'Reset in corso...', success: `Password resettata a "123456"!`, error: 'Errore.' }
            );
        } catch(error) { console.error(error); } finally { setResetPwdModal({isOpen: false, staff: null}); }
    };

    return (
        <div style={{ maxWidth: '1300px', margin: '20px auto', padding: '0 20px' }}>
            <h2 style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#111827', marginBottom: '1.5rem' }}>Gestione Personale</h2>
            
            {/* -------------------- MENU TABS -------------------- */}
            <div style={{ display: 'flex', gap: '1rem', borderBottom: '1px solid #e5e7eb', marginBottom: '2rem', overflowX: 'auto', paddingBottom: '5px' }}>
                <TabButton label="Staff & Segreteria" icon={<Users size={18}/>} isActive={activeTab === 'staff'} onClick={() => setActiveTab('staff')} />
                <TabButton label="Medici Specialisti" icon={<Stethoscope size={18}/>} isActive={activeTab === 'doctors'} onClick={() => setActiveTab('doctors')} />
                <TabButton label="Profili Contrattuali" icon={<Briefcase size={18}/>} isActive={activeTab === 'contracts'} onClick={() => setActiveTab('contracts')} />
            </div>

            {/* -------------------- TAB 1: STAFF (ORIGINALE) -------------------- */}
            {activeTab === 'staff' && (
                <div style={{ animation: 'fadeIn 0.3s ease' }}>
                    
                    {/* Modali originali dello Staff */}
                    {isModalOpen && (
                        <StaffModal staffData={editingStaff} onSave={handleSaveStaff} onClose={() => setIsModalOpen(false)} currentUserProfile={userProfile} />
                    )}
                    {associationModal.isOpen && (
                        <AssociateAccountModal personName={associationModal.staff.name} onConfirmNew={handleAssociateAccount} onClose={() => setAssociationModal({ isOpen: false, staff: null })} isSubmitting={isCreatingAccount} />
                    )}
                    {resetPwdModal.isOpen && (
                        <ConfirmationModal title="Conferma Reset Password" onConfirm={() => handleResetPassword(resetPwdModal.staff)} onClose={() => setResetPwdModal({isOpen: false, staff: null})}>
                            <p>Sei sicuro di voler impostare la password di <b>{resetPwdModal.staff.name}</b> a "123456"?</p><p>L'utente dovrà cambiarla al suo prossimo accesso.</p>
                        </ConfirmationModal>
                    )}
                    {modal.type === 'recalc-staff-slots' && (
                        <ChoiceModal
                            title={`Modifica Orari per ${modal.data.userName}`}
                            onClose={() => { setModal({ type: null, data: null }); fetchStaff(); }} 
                            onChoice={(choice) => handleRecalculateChoice(choice, modal.data)}
                            choices={[
                                { label: 'Solo da Oggi (Applica al Futuro)', value: 'future_only', className: 'confirm-button' },
                                { label: 'Storico + Futuro (Ricalcola tutto)', value: 'retrospective', className: 'warning-button' }
                            ]}
                        >
                            <p>Hai modificato le fasce orarie di <b>{modal.data.userName}</b>. Questa modifica influenzerà il calcolo delle ore.</p>
                            <p>Vuoi applicare retroattivamente questi nuovi orari anche ai turni già completati o solo da oggi in poi?</p>
                        </ChoiceModal>
                    )}

                    <div className="management-container" style={{ margin: 0, padding: 0, maxWidth: '100%', boxShadow: 'none', border: 'none', backgroundColor: 'transparent' }}>
                        <div className="management-header">
                            <h4>Staff Interno</h4>
                            <div style={{ display: 'flex', gap: '10px' }}>
                                {staffPermission === 'completo' && (
                                    <button onClick={handleNewClick} className="add-new-button">+ Aggiungi Persona</button>    
                                )}
                            </div>
                        </div>

                        <div className="management-list">
                            {staffList.map(staff => (
                                <div key={staff.id} className="management-card">
                                    <div className="list-item-content">
                                        <div className="card-top-row">
                                            <span className="list-item-color-swatch" style={{ backgroundColor: staff.color }}></span>
                                            <span className="list-item-main-text">{staff.name}</span>
                                            <span className="list-item-sub-text">{staff.email || 'Nessun account associato'}</span>
                                        </div>
                                        <span className="list-item-sub-text">{staff.role}</span>
                                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '10px'}}>
                                            <span className="list-item-sub-text"><strong>{staff.timeSlots?.length || 0}</strong> fasce orarie.</span>
                                            <div style={{display: 'flex', gap: '5px'}}>
                                                <button onClick={() => handleCopySlots(staff.timeSlots)} className="icon-button" title="Copia Orari">📋</button>
                                                <button onClick={() => handlePasteSlots(staff.id)} className="icon-button" title="Incolla Orari" disabled={!copiedSlots}>📥</button>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="list-item-actions" style={{width: '100%', display: 'flex', flexDirection: 'column', gap: '8px'}}>
                                        {staffPermission === 'completo' && staff.email && (
                                            <div style={{display: 'flex', gap: '8px', width: '100%'}}>
                                                <button style={{flex: 1}} onClick={() => handleChangeEmail(staff)} className="neutral-button">Cambia Email</button>
                                                <button style={{flex: 1}} onClick={() => setResetPwdModal({isOpen: true, staff: staff})} className="warning-button">Reset Password</button>
                                            </div>
                                        )}
                                        
                                        <div style={{display: 'flex', gap: '8px', width: '100%'}}>
                                            {!staff.email && staffPermission === 'completo' && (
                                            <button style={{flex: 1}} onClick={() => setAssociationModal({ isOpen: true, staff: staff })} className="duplicate-button">Crea Account</button>
                                            )}
                                        </div>
                                
                                        <div style={{display: 'flex', gap: '8px', width: '100%'}}>
                                        {(staffPermission === 'completo' || staffPermission === 'semi-completo' || (staffPermission === 'self-edit' && userProfile.uid === staff.id)) && (
                                            <button style={{flex: 1}} onClick={() => handleEditClick(staff)} className="info-button">Modifica Dati</button>
                                        )}
                                        {staffPermission === 'completo' && (
                                            <button style={{flex: 1}} onClick={() => handleDeleteStaff(staff.id)} className="delete-button">Elimina</button>
                                        )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* -------------------- TAB 2: MEDICI -------------------- */}
            {activeTab === 'doctors' && (
                <div style={{ animation: 'fadeIn 0.3s ease' }}>
                    {/* Renderizza il componente ManageDoctors nativamente */}
                    <ManageDoctors />
                </div>
            )}

            {/* -------------------- TAB 3: CONTRATTI -------------------- */}
            {activeTab === 'contracts' && (
                <div style={{ animation: 'fadeIn 0.3s ease' }}>
                    {/* Renderizza il componente ManageContractProfiles nativamente */}
                    <ManageContractProfiles />
                </div>
            )}
        </div>
    );
}

export default ManageStaff;